import {
	OfferType,
	PAGES,
	ProjectFilterValues,
	ProjectItem,
	ProjectsResponse,
	PropositionItem,
} from "@dune/types";
import { Card, CardContent, CardHeader, CardTitle, cn } from "@dune/ui";
import { projectFilter } from "@dune/vars";
import { format } from "date-fns";
import React from "react";
import { useNavigate } from "react-router-dom";

const getStatusColor = (offerTypes: OfferType[]) => {
	if (offerTypes.length > 0) return "bg-blue-100 text-blue-800";
	return "bg-gray-100 text-gray-800";
};

const formatAmount = (amount?: number) => {
	if (!amount) return "";
	return new Intl.NumberFormat("fr-FR", {
		style: "currency",
		currency: "EUR",
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(amount);
};

interface ProjectsListProps {
	activeFilter: ProjectFilterValues;
	projectsData?: ProjectsResponse;
	isLoading: boolean;
	error: Error | null;
}

export const ProjectsList: React.FC<ProjectsListProps> = ({
	activeFilter,
	projectsData,
	isLoading,
	error,
}) => {
	const navigate = useNavigate();

	const handleProjectClick = (project: ProjectItem | PropositionItem) => {
		if (project.propositionId != null) {
			navigate(
				PAGES.PROPOSALS.show.withParams(project.propositionId.toString()),
			);
		} else {
			if (project.offerTypes.includes(OfferType.GFA)) {
				navigate(PAGES.GFA.show.withParams(project.id.toString()));
			} else {
				navigate(PAGES.PROJECTS.show.withParams(project.id.toString()));
			}
		}
	};

	const filterProjectsByOfferType = (
		projects: ProjectItem[] | PropositionItem[],
		filter: ProjectFilterValues,
	) => {
		if (filter === projectFilter.TOUS) return projects;
		return projects.filter((project) =>
			project.offerTypes.some(
				(type) => type.toLowerCase() === filter.toLowerCase(),
			),
		);
	};

	if (error) {
		return (
			<div className="h-full flex items-center justify-center">
				<div className="text-red-600">
					Erreur lors du chargement des projets
				</div>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="h-full flex items-center justify-center">
				<div className="text-gray-500">Chargement des projets...</div>
			</div>
		);
	}

	const projectCategories = [
		{
			title: "Projets en cours",
			count: filterProjectsByOfferType(
				projectsData?.projects.inProgress || [],
				activeFilter,
			).length,
			badgeColor: "border-blue-500 bg-blue-50 text-blue-700",
			projects: filterProjectsByOfferType(
				projectsData?.projects.inProgress || [],
				activeFilter,
			),
		},
		{
			title: "Propositions en cours",
			count: filterProjectsByOfferType(
				projectsData?.projects.propositions || [],
				activeFilter,
			).length,
			badgeColor: "border-purple-500 bg-purple-50 text-purple-700",
			projects: filterProjectsByOfferType(
				projectsData?.projects.propositions || [],
				activeFilter,
			),
		},
		{
			title: "Contrats en cours",
			count: filterProjectsByOfferType(
				projectsData?.projects.contracts || [],
				activeFilter,
			).length,
			badgeColor: "border-green-500 bg-green-50 text-green-700",
			projects: filterProjectsByOfferType(
				projectsData?.projects.contracts || [],
				activeFilter,
			),
		},
		{
			title: "À réceptionner",
			count: filterProjectsByOfferType(
				projectsData?.projects.reception || [],
				activeFilter,
			).length,
			badgeColor: "border-orange-500 bg-orange-50 text-orange-700",
			projects: filterProjectsByOfferType(
				projectsData?.projects.reception || [],
				activeFilter,
			),
		},
	];

	return (
		<div className="h-full overflow-x-auto">
			<div className="flex min-w-fit h-full pb-4">
				{projectCategories.map((category) => (
					<div key={category.title} className="w-80 flex-shrink-0">
						<Card className="h-full flex flex-col overflow-hidden bg-transparent border-none shadow-none py-4">
							<CardHeader className="flex-shrink-0 px-4 gap-0">
								<CardTitle className="flex items-center gap-3">
									<div
										className={`rounded-full px-3 py-1 border text-sm font-medium ${category.badgeColor}`}
									>
										{category.title}
									</div>
									<span className="text-gray-500 text-sm">
										{category.count}
									</span>
								</CardTitle>
							</CardHeader>
							<CardContent className="flex-1 overflow-hidden px-4">
								{category.projects.length === 0 ? (
									<div className="flex items-center justify-center h-32 text-gray-500 text-sm">
										{activeFilter === projectFilter.TOUS
											? "Aucun projet dans cette catégorie"
											: `Aucun projet ${activeFilter.toUpperCase()} dans cette catégorie`}
									</div>
								) : (
									<div className="h-full overflow-y-auto space-y-3 pr-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
										{category.projects.map((project) => (
											<Card
												key={project.id}
												className="p-3 bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer hover:border-gray-300"
												onClick={() => handleProjectClick(project)}
											>
												<div className="space-y-2">
													<div className="flex items-start justify-between">
														<div className="flex-1 min-w-0">
															<p className="font-medium text-sm text-gray-900 truncate">
																{project.title}
															</p>
															<p className="text-xs text-gray-600 truncate">
																{project.clientName}
															</p>
														</div>
													</div>

													<div className="flex flex-wrap gap-1">
														{project.offerTypes.map((type, index) => (
															<span
																key={`${project.id}-${type}-${index}`}
																className={cn(
																	"text-xs font-medium px-2 py-1 rounded",
																	getStatusColor([type]),
																)}
															>
																{type}
															</span>
														))}
													</div>

													{project.totalAmount && (
														<div className="text-xs text-gray-600">
															<span className="font-medium">
																{formatAmount(project.totalAmount)}
															</span>
														</div>
													)}

													<div className="text-xs text-gray-500">
														Créé le{" "}
														{format(new Date(project.createdAt), "dd/MM/yyyy")}
													</div>
												</div>
											</Card>
										))}
									</div>
								)}
							</CardContent>
						</Card>
					</div>
				))}
			</div>
		</div>
	);
};
